import api from './api';

const base = (venueId: string, wineListId: string) =>
  `/admin/venue/${venueId}/wine-list/${wineListId}/sync`;

// ── DTOs (mirrors backend) ────────────────────────────────────────────────────

export interface WineListSyncExportResponse {
  headers: string[];
  formatHeaders: string[];
  rows: Array<Record<string, string | number>>;
}

export interface WineListSyncFormatPriceCell {
  header: string;
  value: string;
}

export interface WineListSyncResolved {
  existingItemId: string | null;
  wineId: string | null;
  sectionId: string | null;
  sectionWillBeCreated: boolean;
  sectionName: string | null;
  formatIdByHeader: Record<string, string>;
}

export interface WineListSyncRowData {
  wineListItemId: string;
  wineId: string;
  inventoryItemId: string;
  section: string;
  position: string;
  wineName: string;
  producer: string;
  vintage: string;
  region: string;
  bottleSize: string;
  bottlePrice: string;
  currency: string;
  visibility: string;
  notes: string;
  externalUrl: string;
  scoreSource: string;
  score: string;
  formatPrices: WineListSyncFormatPriceCell[];
}

export interface WineListSyncPreviewRow {
  rowNumber: number;
  action: 'CREATE' | 'UPDATE' | 'ERROR';
  warnings: string[];
  errors: string[];
  resolved: WineListSyncResolved;
  data: WineListSyncRowData;
}

export interface WineListSyncPreviewResponse {
  totalRows: number;
  summary: { create: number; update: number; error: number };
  rows: WineListSyncPreviewRow[];
  formatHeaders: string[];
}

export interface WineListSyncCommitRow {
  rowNumber: number;
  action: 'CREATE' | 'UPDATE' | 'ERROR';
  status: 'SUCCESS' | 'ERROR' | 'SKIPPED';
  errors: string[];
  warnings: string[];
  result: { itemId: string | null; sectionCreated: boolean; formatPricesUpdated: number };
}

export interface WineListSyncCommitResponse {
  totalRows: number;
  summary: { created: number; updated: number; errored: number; skipped: number };
  rows: WineListSyncCommitRow[];
}

// ── Error mapping ─────────────────────────────────────────────────────────────

export interface WineListSyncError {
  code: string;
  message: string;
  missing?: string[];
}

const extractError = (err: any): WineListSyncError => {
  const data = err?.response?.data;
  if (data?.code) return data as WineListSyncError;
  if (data?.message) return { code: 'UNKNOWN', message: data.message };
  return { code: 'UNKNOWN', message: err?.message ?? 'Request failed' };
};

// ── Service ───────────────────────────────────────────────────────────────────

const wineListSyncService = {
  /** Returns the existing items in the import-shape headers + row dicts. */
  exportItems: async (venueId: string, wineListId: string): Promise<WineListSyncExportResponse> => {
    const response = await api.get(`${base(venueId, wineListId)}/export`);
    return response.data;
  },

  /** Preview an import file. No side effects. Throws WineListSyncError on file-level problems. */
  preview: async (venueId: string, wineListId: string, file: File): Promise<WineListSyncPreviewResponse> => {
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await api.post(`${base(venueId, wineListId)}/preview`, form, {
        // Let the browser set the multipart boundary header — explicit override clears the default JSON content-type from the api client.
        headers: { 'Content-Type': undefined as any },
      });
      return response.data;
    } catch (err) {
      throw extractError(err);
    }
  },

  /** Commit the import. Re-uploads the file to keep the request idempotent-shaped. */
  commit: async (venueId: string, wineListId: string, file: File): Promise<WineListSyncCommitResponse> => {
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await api.post(`${base(venueId, wineListId)}/commit`, form, {
        headers: { 'Content-Type': undefined as any },
      });
      return response.data;
    } catch (err) {
      throw extractError(err);
    }
  },
};

export default wineListSyncService;
